import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Container,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  useMediaQuery,
  Tooltip,
  Avatar,
  Divider
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Add as AddIcon,
  Receipt as ReceiptIcon,
  People as PeopleIcon,
  Menu as MenuIcon,
  DirectionsCar as CarIcon,
  Settings as SettingsIcon,
  Business as BusinessIcon
} from '@mui/icons-material';
import { Link, useLocation, useNavigate } from 'react-router-dom';

const Navigation = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const isActive = (path) => location.pathname === path;

  const navigationItems = [
    { path: '/', label: 'Dashboard', icon: <DashboardIcon />, color: 'primary' },
    { path: '/invoice/new', label: 'New Invoice', icon: <AddIcon />, color: 'success' },
    { path: '/invoices', label: 'Invoices', icon: <ReceiptIcon />, color: 'info' },
    { path: '/customers', label: 'Customers', icon: <PeopleIcon />, color: 'secondary' },
  ];

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleNavigation = (path) => {
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const drawer = (
    <Box sx={{ width: 250 }}>
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Avatar sx={{ 
          bgcolor: theme.palette.primary.main, 
          width: 56, 
          height: 56,
          mx: 'auto',
          mb: 1
        }}>
          <BusinessIcon sx={{ fontSize: 32 }} />
        </Avatar>
        <Typography variant="h6" color="primary" sx={{ fontWeight: 600 }}>
          Moga Auto
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Invoicing System
        </Typography>
      </Box>
      <Divider />
      <List>
        {navigationItems.map((item) => (
          <ListItem
            key={item.path}
            button
            onClick={() => handleNavigation(item.path)}
            sx={{
              backgroundColor: isActive(item.path) ? theme.palette.action.selected : 'transparent',
              '&:hover': {
                backgroundColor: theme.palette.action.hover,
              },
              borderRadius: 1,
              mx: 1,
              mb: 0.5,
            }}
          >
            <ListItemIcon sx={{ color: isActive(item.path) ? theme.palette[item.color].main : 'inherit' }}>
              {item.icon}
            </ListItemIcon>
            <ListItemText 
              primary={item.label} 
              sx={{ 
                fontWeight: isActive(item.path) ? 600 : 400,
                color: isActive(item.path) ? theme.palette[item.color].main : 'inherit'
              }}
            />
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <>
      <AppBar 
        position="static" 
        elevation={0}
        sx={{ 
          backgroundColor: 'white',
          color: 'text.primary',
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Container maxWidth="xl">
          <Toolbar sx={{ px: { xs: 1, sm: 2 } }}>
            {isMobile && (
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="start"
                onClick={handleDrawerToggle}
                sx={{ mr: 2 }}
              >
                <MenuIcon />
              </IconButton>
            )}

            <Typography 
              variant="h6" 
              component="div" 
              sx={{ 
                flexGrow: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                color: 'primary.main',
                fontWeight: 600,
                cursor: 'pointer'
              }}
              onClick={() => navigate('/')}
            >
              <BusinessIcon sx={{ fontSize: 28 }} />
              Moga Auto Invoicing
            </Typography>

            {!isMobile && (
              <Box sx={{ display: 'flex', gap: 1 }}>
                {navigationItems.map((item) => (
                  <Tooltip key={item.path} title={item.label} arrow>
                    <Button
                      variant={isActive(item.path) ? "contained" : "text"}
                      color={isActive(item.path) ? item.color : "inherit"}
                      startIcon={item.icon}
                      onClick={() => handleNavigation(item.path)}
                      sx={{
                        borderRadius: 2,
                        px: 2,
                        py: 1,
                        textTransform: 'none',
                        fontWeight: 500,
                        minWidth: 'auto',
                        '&:hover': {
                          backgroundColor: isActive(item.path) 
                            ? theme.palette[item.color].dark 
                            : theme.palette.action.hover,
                          transform: 'translateY(-1px)',
                          transition: 'all 0.2s ease-in-out',
                        },
                        transition: 'all 0.2s ease-in-out',
                      }}
                    >
                      {item.label}
                    </Button>
                  </Tooltip>
                ))}
              </Box>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { 
            boxSizing: 'border-box', 
            width: 250,
            border: 'none',
            boxShadow: theme.shadows[8],
          },
        }}
      >
        {drawer}
      </Drawer>
    </>
  );
};

export default Navigation;
